import moment from 'moment';
import { isNumber } from 'lodash';
import downloadFile from 'downloadjs';

import { danger, info } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import type { Action, Dispatch, ProposalPageFilterOption, ProposalPageSortOption, ThunkAction } from 'types';
import type { ProposalTileChannelTypeOptions } from 'modules/proposal/types/ProposalTile.type';
import type { ProposalReportState } from 'modules/proposal/types/Proposal.type';

applyTranslations(require('./proposal-report.i18n.json'));

const { CREATOR_PROPOSAL } = API.END_POINTS;

const STATUS_SET = 'creatorProposalReport/STATUS_SET';

const initialState: ProposalReportState = {
  isDownloading: false
};

function setStatus(isDownloading: boolean): Action {
  return {
    type: STATUS_SET,
    payload: {
      isDownloading
    }
  };
}

const downloadCustomReportAsync = (
  taskId: number
): ThunkAction => (
  async (dispatch: Dispatch) => {
    const date = moment().format('YYYY-MM-DD');
    const fileName = `Collabary Creator Proposal ${date}.pptx`;

    const url = CREATOR_PROPOSAL.downloadCreatorReport(taskId);

    try {
      const response = await get(url, { responseType: 'blob' }, { suppressGlobalNotification: true });

      const { data } = response as { data: Blob };
      downloadFile(data, fileName);
    } catch (err) {
      logError(err);
      dispatch(danger(__t('proposal_report_reducer.loading_report_error')));
    } finally {
      dispatch(setStatus(false));
    }
  }
);

const ASYNC_DOWNLOAD_INTERVAL_TIME = 3000;
let ASYNC_DOWNLOAD_INTERVAL_ID: number;

function clearDownloadTimeout() {
  window.clearTimeout(ASYNC_DOWNLOAD_INTERVAL_ID);
}

const SORT_MAP = {
  ig_asc: 'ig_followers.asc',
  ig_desc: 'ig_followers.desc',
  tt_asc: 'tt_followers.asc',
  tt_desc: 'tt_followers.desc'
};

export function downloadCreatorProposal(
  organizationId: number,
  listId: number,
  sortOrder: ProposalPageSortOption,
  currentFilter: ProposalPageFilterOption
): ThunkAction {
  return async (dispatch, getState) => {
    const listInfo = getState().proposalCreatePageInfo.info;

    dispatch(setStatus(true));
    try {
      const possibleChannels: Array<ProposalTileChannelTypeOptions> = ['instagram', 'tiktok', 'custom'];
      const channelsSelection = possibleChannels
        .reduce<Record<string, boolean>>((result, entry) => ({
          ...result,
          [entry]: Boolean(listInfo?.channels.includes(entry))
        }), {});

      const data = {
        reportType: listInfo?.templateType,
        channelsSelection,
        statuses: (currentFilter === 'all')
          ? ['approved', 'declined', 'pending', 'incomplete']
          : [currentFilter],
        sortOrder: SORT_MAP[sortOrder]
      };

      const url = CREATOR_PROPOSAL.generateCreatorReport(organizationId, listId);
      dispatch(info(__t('proposal_report_reducer.generating_report_info'), { hideDuration: 5000 }));
      const taskIdResponse = await post(url, { data }, { suppressGlobalNotification: true });

      const taskId = taskIdResponse.data?.taskId;

      if (taskId) {
        const statusUrl = CREATOR_PROPOSAL.checkCreatorReport(taskId);

        if (isNumber(ASYNC_DOWNLOAD_INTERVAL_ID)) {
          clearDownloadTimeout();
        }

        // keep calling endpoint until status is 'ready' or 'error'
        const asyncDownloadHandler = async () => {
          const statusResponse = await get(statusUrl, {}, { suppressGlobalNotification: true });
          const status = statusResponse.data?.status;

          if (status === 'ready') {
            clearDownloadTimeout();
            dispatch(downloadCustomReportAsync(taskId));

            return;
          }

          if (status === 'error') {
            dispatch(setStatus(false));
            clearDownloadTimeout();
            logError(__t('proposal_report_reducer.generation_report_error'));
            dispatch(danger(__t('proposal_report_reducer.generation_report_error')));

            return;
          }

          ASYNC_DOWNLOAD_INTERVAL_ID = window.setTimeout(asyncDownloadHandler, ASYNC_DOWNLOAD_INTERVAL_TIME);
        };
        ASYNC_DOWNLOAD_INTERVAL_ID = window.setTimeout(asyncDownloadHandler, ASYNC_DOWNLOAD_INTERVAL_TIME);
      } else {
        dispatch(setStatus(false));
        logError('proposal_report_reducer.no_task_id_error');
        dispatch(danger(__t('proposal_report_reducer.no_task_id_error')));
      }
    } catch (error) {
      logError(error);
      dispatch(setStatus(false));
    }
  };
}

export default createReducer<ProposalReportState, any>('proposalReport', initialState, {
  [STATUS_SET]: (state, { isDownloading }) => ({
    ...state,
    isDownloading
  })
});
